"use client";

import React, { useState } from "react";
import { Section, Typography } from "@/components/ui";
import Image from "next/image";

interface SectorContent {
  title: string;
  items: {
    image: string;
    title: string;
    description: string;
  }[];
}

interface SectorBenefitsTabsProps {
  sectorBenefits: SectorContent[];
}

export default function SectorBenefitsTabs({ sectorBenefits }: SectorBenefitsTabsProps) {
  const [activeTab, setActiveTab] = useState(0);

  return (
    <Section variant="default" background="white" id="sectorBenefits">
      <div className="text-center mb-10">
        <Typography variant="h2" className="mb-4">
          Sector-wise Partnering Benefits
        </Typography>
      </div>

      <div className="grid md:grid-cols-12 gap-8">
        {/* Tabs Sidebar */}
        <div className="md:col-span-5">
          <div className="space-y-2" role="tablist" aria-label="Sector benefits tabs">
            {sectorBenefits.map((sector, index) => (
              <button
                key={index}
                type="button"
                role="tab"
                id={`sector-tab-${index}`}
                aria-selected={activeTab === index}
                aria-controls={`sector-panel-${index}`}
                onClick={() => setActiveTab(index)}
                className={`w-full text-left p-4 rounded-lg cursor-pointer transition-all border-l-4 ${
                  activeTab === index
                    ? "bg-gray-50 border-gray-600"
                    : "bg-white hover:bg-gray-50 border-transparent"
                }`}
              >
                <Typography
                  variant="h3"
                  className={`text-lg ${
                    activeTab === index ? "text-black" : "text-gray-700"
                  }`}
                >
                  {sector.title}
                </Typography>
              </button>
            ))}
          </div>
        </div>

        {/* Tab Content */}
        <div className="md:col-span-7">
          <div className="space-y-6">
            {sectorBenefits.map((sector, sectorIndex) => (
              <div
                key={sectorIndex}
                id={`sector-panel-${sectorIndex}`}
                role="tabpanel"
                aria-labelledby={`sector-tab-${sectorIndex}`}
                hidden={activeTab !== sectorIndex}
                className={activeTab === sectorIndex ? "block" : "hidden"}
              >
                <div className={sector.items.length === 1 ? "max-w-xl" : "grid md:grid-cols-2 gap-6"}>
                  {sector.items.map((item, itemIndex) => (
                    <div key={itemIndex}>
                      <div className="mb-4">
                        <Image
                          src={item.image}
                          alt={item.title}
                          title={item.title}
                          width={600}
                          height={450}
                          className="w-full h-auto rounded-lg"
                        />
                      </div>
                      <Typography variant="h4" className="mb-3 font-semibold text-base">
                        {item.title}
                      </Typography>
                      <Typography variant="body" className="text-gray-600 text-sm">
                        {item.description}
                      </Typography>
                    </div>
                  ))}
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </Section>
  );
}
